// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## voice.ts
//
// A Voice object is a holder for measures for one voice.  It is
// assigned to a staff for rendering.
//
// It also holds default beaming & stem-direction preferences for
// notes within this voice.

import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import type { NotePointers } from './measure';
import Measure from './measure';
import utils from '../utils';
import Staff from './staff';
import TablaInstrument from './tablaInstrument';
import Music from './music';
import PianoInstrument from './pianoInstrument';
import MusicObject from './musicObject';
import Interval from './interval';
import type Clef from './clef';
import type { MidiMessage } from './soundBufferHolder';
import type { AriaStringOptions } from './note';
import type NoteHolder from './noteHolder';
import type Pointer from './pointer';
import type KeySignature from './keySignature';
import type CustomKeySignature from './customKeySignature';

// Object-globals:
const beamingDefault = 0;
// -1 : do not beam to next
// 0 : default metric beaming
// 1 : force beam to next
// overriden by -1 or 1 in a noteHolder.rhythm object

const stemDefault = 0;
// 1 : stems up
// 0 : auto stems
// -1 : stems down
// overriden by -1 or 1 in a noteHolder.beam variable

export default class Voice extends MusicObject {
  measures: Measure[] = [];

  beamingDefault: -1 | 0 | 1 = beamingDefault;

  stemDefault: -1 | 0 | 1 = stemDefault;

  staff?: Staff;

  instrument?: TablaInstrument | PianoInstrument;

  music?: Music;

  // Accepts measures as each argument, or arrays of measures.
  constructor(...measureOrMeasureArray: (Measure | Measure[])[]) {
    super();
    this.measures = utils.flatten(measureOrMeasureArray);
  }

  setMeasure(measure: Measure, index: number): this {
    if (!(measure instanceof Measure)) {
      throw new Error(`Measure must be an instance of measure not ${typeof measure}, ${measure}`);
    }
    if (typeof index !== 'number') {
      throw new Error(`index must be a number, not ${typeof index}, ${index}`);
    }
    this.measures[index] = measure;
    return this;
  }

  getMeasure(index: number): Measure | false {
    if (typeof this.measures[index] !== 'undefined') return this.measures[index]!;
    if (typeof index !== 'number') {
      throw new Error(`index must be a number, not ${typeof index}, ${index}`);
    }
    return false;
  }

  getNumberOfMeasures(): number {
    return this.measures.length;
  }

  push(measure: Measure): this {
    if (!(measure instanceof Measure)) {
      throw new Error(`Measure must be an instance of measure not ${typeof measure}, ${measure}`);
    }
    this.measures.push(measure);
    return this;
  }

  /** Insert a measure before an index or measure */
  insertMeasure(beforeIndexOrMeasure: Measure | number, measure: Measure): this {
    this.splice(beforeIndexOrMeasure, 0, measure);
    return this;
  }

  /**
   * splice measures into a voice
   * @param indexOrMeasure - before which to replace a measure
   * @param howManyToReplace - number of measures to replace
   * @param replacementMeasures - the measures to insert/replace (if any)
   */
  splice(
    indexOrMeasure: number | Measure,
    howManyToReplace: number,
    ...replacementMeasures: (Measure | Measure[])[]
  ): this {
    // follows exact same approach as JS splice function
    const index = this.measureLookup(indexOrMeasure);
    if (index === false) {
      throw new Error(`Unable to find index for measure ${indexOrMeasure}, cannot splice.`);
    }

    const measures: Measure[] = utils.flatten(replacementMeasures);

    // Make the Splice:
    this.measures.splice(index, howManyToReplace, ...measures);
    return this;
  }

  /** returns index of a particular measure */
  private measureLookup(indexOrMeasure: number | Measure): number | false {
    if (typeof indexOrMeasure === 'number') return indexOrMeasure;

    if (indexOrMeasure instanceof Measure) {
      const index = this.measures.findIndex((m) => m === indexOrMeasure);
      return index === -1 ? false : index;
    }
    throw new Error(
      `indexOrMeasure must be of type number | Measure, not ${typeof indexOrMeasure}, ${indexOrMeasure}`
    );
  }

  transpose(interval: Interval | IntervalName) {
    if (!(interval instanceof Interval)) {
      interval = new Interval(interval);
    }

    this.measures.forEach((measure) => measure.transpose(interval));
  }

  /** Set the staff on which this voice will display */
  setStaff(staff: Staff): this {
    if (!(staff instanceof Staff)) {
      throw new Error(`staff must be an instance of Staff, not ${typeof staff}, ${staff}`);
    }
    this.staff = staff;
    return this;
  }

  getStaff(): Staff | undefined {
    return this.staff;
  }

  unsetMusic(): this {
    this.music = undefined;
    return this;
  }

  setMusic(music: Music): this {
    if (!(music instanceof Music)) {
      throw new Error('setMusic must take a Music object as a parameter.');
    }
    this.music = music;
    return this;
  }

  getMusic(): Music | undefined {
    return this.music;
  }

  setStemDefault(direction: -1 | 0 | 1) {
    if (direction < -1 || direction > 1) throw new Error('Stem direction must be -1, 0, or 1');
    this.stemDefault = direction;
    return this;
  }

  getStemDefault(): -1 | 0 | 1 {
    return this.stemDefault;
  }

  setBeamingDefault(beam: -1 | 0 | 1): this {
    if (beam < -1 || beam > 1) throw new Error('Beam direction must be -1, 0, or 1');
    this.beamingDefault = beam;
    return this;
  }

  getBeamingDefault(): -1 | 0 | 1 {
    return this.beamingDefault;
  }

  setInstrument(instrument: PianoInstrument | TablaInstrument) {
    if (instrument instanceof TablaInstrument || instrument instanceof PianoInstrument) {
      this.instrument = instrument;
    }
  }

  getMidiMessages(): MidiMessage[] {
    const midiMessages: MidiMessage[] = [];

    const createMidiMessage = (
      noteHolder: NoteHolder,
      startPointer: Pointer,
      stopPointer: Pointer
    ) => {
      const startBeat = startPointer.toBeats();
      let stopBeat = stopPointer.toBeats();
      const velocity = typeof noteHolder.velocity !== 'undefined' ? noteHolder.velocity : 0.8;
      if (noteHolder.staccato) {
        stopBeat = startBeat + (stopBeat - startBeat) * noteHolder.staccato;
      }
      noteHolder.notes.forEach((note) => {
        const noteVelocity = typeof note.velocity !== 'undefined' ? note.velocity : velocity;
        midiMessages.push({
          startBeat,
          startPointer,
          stopBeat,
          stopPointer,
          midiNumber: note.getMidiNumber(),
          velocity: noteVelocity,
        });
      });
    };

    const pointers = this.getPointers();
    pointers.forEach((p) => {
      if (!p.noteHolder.rhythm) {
        throw Error('noteHolder rhythm must be provided to get voice pointers.');
      }
      if (p.noteHolder.rhythm.isTied && midiMessages.length) {
        p.startPointer = midiMessages.pop()!.startPointer!;
      }
      createMidiMessage(p.noteHolder, p.startPointer, p.stopPointer);
    });
    return midiMessages;
  }

  getPointers(music: Music | undefined = this.music): NotePointers[] {
    if (!music || !(music instanceof Music)) {
      throw new Error(
        'voice.music must be set, or a music parameter must be passed, in order to get pointers on a voice. (This allows access to the time signatures for the music.) Use voices.setMusic(music).'
      );
    }
    if (!music.getTimeSignature(0)) {
      throw new Error(
        'Music must have at least one time signature set in order for a voice to get pointers.'
      );
    }

    return this.measures.reduce(
      (pointers: NotePointers[], measure: Measure, measureNumber: number) =>
        pointers.concat(measure.getPointers(music.getTimeSignature(measureNumber)!, measureNumber)),
      []
    );
  }

  /**
   * Returns contiguous pointers as though the voice had no rests in it, only notes.
   */
  getPerformancePointers(): NotePointers[] {
    const p = this.getPointers();
    for (let i = 0; i < p.length; i++) {
      // If it's tied or a rest, extend the previous note's stop pointer.
      if (p[i]!.noteHolder.rhythm!.isTied || p[i]!.noteHolder.length === 0) {
        if (i > 0) p[i - 1]!.stopPointer = p[i]!.stopPointer;
        p.splice(i, 1);
        i--;
      }
    }
    return p;
  }

  /**
   * Returns noncontigous pointers for each sounded note, ending at any following
   * printed rest.
   */
  getPerformancePointersWithSilences(): NotePointers[] {
    const p = this.getPointers();
    for (let i = 0; i < p.length; i++) {
      // If it's tied or a rest, extend the previous note's stop pointer.
      if (p[i]!.noteHolder.length === 0) {
        p.splice(i, 1);
        i--;
        continue;
      }

      if (p[i]!.noteHolder.rhythm!.isTied) {
        if (i > 0) p[i - 1]!.stopPointer = p[i]!.stopPointer;
        p.splice(i, 1);
        i--;
      }
    }
    return p;
  }

  toAriaString(
    ariaOptions: AriaStringOptions = {},
    clef?: Clef,
    keySignature?: KeySignature | CustomKeySignature
  ) {
    const { measureNumber = this.measures.length > 1 } = ariaOptions;
    if (!this.measures.length) return '';
    return this.measures
      .map((measure, measureIndex) => {
        const nextMeasure: Measure | undefined = this.measures[measureIndex + 1];
        const suppressEndingBarline = nextMeasure?.style?.beginningBarline === 'repeat_begin';
        let measureText = '';
        if (measureNumber) {
          measureText += `Measure ${measureIndex + 1}:`;
        }
        const measureContentText = measure.toAriaString(
          ariaOptions,
          clef,
          suppressEndingBarline,
          this,
          keySignature
        );
        measureText += measureContentText && measureText ? ' ' : '';
        measureText += measureContentText;
        if (this.measures.length > 1 && measureText) measureText += '.';
        return measureText;
      })
      .join(' ');
  }

  toString(includeProperties = false) {
    let str = '';
    let seperator = '';
    // Has to be for loop to catch empty/undefined measures which could exist:
    for (let i = 0; i < this.measures.length; i++) {
      if (typeof this.measures[i] !== 'undefined') {
        str += seperator + this.measures[i]!.toString(includeProperties);
      } else str += `${seperator}|`;
      seperator = ' ';
      // If the next barline begins w/ a repeat opening,
      // omit the prior ending barline:
      if (
        typeof this.measures[i + 1] !== 'undefined' &&
        typeof this.measures[i + 1]!.style !== 'undefined' &&
        this.measures[i + 1]!.style.beginningBarline === 'repeat_begin'
      ) {
        str = str.substring(0, str.length - 2);
      }
    }
    return str;
  }

  fromString(voiceString: string) {
    Voice.fromString(voiceString, this);
  }

  get isPickup(): boolean {
    const hasMeasures = !!this.measures.length;
    return hasMeasures && this.measures[0]!.isPickup;
  }

  static fromString(voiceString: string, voice = new Voice()) {
    // Find & split on the various barline types:
    // Use 'forbiddens' to avoid double matches
    const barlines = [
      { string: '||: ', indexOffset: 0, forbids: 2 },
      { string: ' |||', indexOffset: 4, forbids: 3 },
      { string: ' ||', indexOffset: 3, forbids: 2 },
      { string: ' :||', indexOffset: 4 },
      { string: ' |', indexOffset: 2 },
    ];

    /**
     * Places where we intend to split the string into measure-long strings.
     */
    let splitPoints: number[] = [];

    /**
     * Places we're forbidden to split as though it were a measure. E.G., we
     * normally split on a |, but some barlines have more than one ||.
     */
    const forbiddens: number[] = [];

    // Finds all indexes of a substring in a string
    const locations = function (needle: string, haystack: string) {
      const a: number[] = [];
      let i = -1;
      // eslint-disable-next-line no-cond-assign
      while ((i = haystack.indexOf(needle, i + 1)) >= 0) a.push(i);
      return a;
    };

    // Uses all found indexes to create final match
    // & forbidden list
    const registerIndexAndNoteForbidden = function (
      matches: number[],
      barline: typeof barlines[number]
    ) {
      matches.forEach((index) => {
        if (barline.forbids) {
          forbiddens.push(barline.forbids + index);
        }
        splitPoints.push(index + barline.indexOffset);
      });
    };

    // Do the searching for barlines!
    barlines.forEach((barline) => {
      const matches = locations(barline.string, voiceString);
      registerIndexAndNoteForbidden(matches, barline);
    });

    // Remove all forbidden split points:
    splitPoints = splitPoints.filter((splitPoint) => !forbiddens.includes(splitPoint));

    // Sort them:
    splitPoints = splitPoints.sort((a, b) => a - b);

    // Split the string into measure strings, & create
    // new measures:
    if (splitPoints[0] !== 0) splitPoints.unshift(0);
    if (splitPoints[splitPoints.length - 1] !== voiceString.length) {
      splitPoints.push(voiceString.length);
    }

    const measures = [];
    for (let i = 1; i < splitPoints.length; i++) {
      const measureString = voiceString.slice(splitPoints[i - 1], splitPoints[i]);
      const measure = Measure.fromString(measureString);
      measures.push(measure);
    }
    voice.measures = measures;
    return voice;
  }
}
